"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";

interface ComingSoonProps {
  /** "Build Your Own Wig", "Our Wigs", "About", "Blog", etc. */
  title: string;
  /**
   * Body copy explaining what's coming. Keep it short — a sentence
   * or two of context, not a sales pitch.
   */
  body?: string;
  /** Primary CTA — typically a path the customer CAN use right now. */
  primaryCta?: { label: string; href: string };
  /** Secondary CTA — optional fallback action (e.g. "Book a consult"). */
  secondaryCta?: { label: string; href: string };
  /**
   * Optional full-bleed background photo. When set, the card renders
   * over the image with a dark scrim and light text.
   */
  backgroundImage?: string;
}

/**
 * Reusable coming-soon page used by every section gated behind a
 * `comingSoon.<section>` flag in SiteConfig. Replaces the inline
 * BuilderComingSoon component that lived in the builder page.
 */
export default function ComingSoon({
  title,
  body,
  primaryCta,
  secondaryCta,
  backgroundImage,
}: ComingSoonProps) {
  const t = useTranslations("comingSoon");
  const hasBg = Boolean(backgroundImage);
  return (
    <div
      className={cn(
        "relative overflow-hidden min-h-[70vh] flex items-center justify-center px-6 py-20",
        hasBg && "bg-brand-dark"
      )}
    >
      {hasBg && (
        <>
          <Image
            src={backgroundImage as string}
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-center"
          />
          <div className="absolute inset-0 bg-brand-dark/70" />
        </>
      )}
      <div className="relative max-w-md text-center">
        <p className="text-[10px] font-semibold tracking-[0.3em] uppercase text-brand-gold mb-4">
          {t("eyebrow")}
        </p>
        <h1
          className={cn(
            "font-display text-3xl sm:text-4xl mb-4",
            hasBg ? "text-white" : "text-brand-dark"
          )}
        >
          {title}
        </h1>
        {body && (
          <p
            className={cn(
              "text-sm leading-relaxed mb-8",
              hasBg ? "text-white/85" : "text-brand-muted"
            )}
          >
            {body}
          </p>
        )}
        {(primaryCta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {primaryCta && (
              <Link
                href={primaryCta.href}
                className={cn(
                  "w-full sm:w-auto sm:min-w-[200px] inline-flex items-center justify-center px-8 py-3 text-sm font-medium tracking-widest uppercase transition-colors",
                  hasBg
                    ? "bg-brand-gold text-white hover:bg-white hover:text-brand-dark"
                    : "bg-brand-dark text-white hover:bg-brand-gold"
                )}
              >
                {primaryCta.label}
              </Link>
            )}
            {secondaryCta && (
              <Link
                href={secondaryCta.href}
                className={cn(
                  "w-full sm:w-auto sm:min-w-[200px] inline-flex items-center justify-center px-8 py-3 border text-sm font-medium tracking-widest uppercase transition-colors",
                  hasBg
                    ? "border-white/40 text-white hover:bg-white hover:text-brand-dark"
                    : "border-brand-dark/30 text-brand-dark hover:bg-brand-dark hover:text-white"
                )}
              >
                {secondaryCta.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
